import { apiClient } from '../config';
import { endpoints } from '../endpoints';

/**
 * DOI service for handling Digital Object Identifier operations
 */
export const doiService = {
  // Configuration operations
  async createConfiguration(configData) {
    const response = await apiClient.post(endpoints.dois.configurations.create, configData);
    return response.data;
  },

  async updateConfiguration(id, configData) {
    const response = await apiClient.put(endpoints.dois.configurations.update(id), configData);
    return response.data;
  },

  async getConfiguration(id) {
    const response = await apiClient.get(endpoints.dois.configurations.getById(id));
    return response.data;
  },

  async getActiveConfiguration() {
    const response = await apiClient.get(endpoints.dois.configurations.getActive);
    return response.data;
  },

  async listConfigurations(params = {}) {
    const { page = 1, limit = 10, agency, isActive } = params;
    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      ...(agency && { agency }),
      ...(isActive !== undefined && { isActive: isActive.toString() }),
    });
    
    const response = await apiClient.get(`${endpoints.dois.configurations.list}?${queryParams}`);
    return response.data;
  },

  async activateConfiguration(id) {
    const response = await apiClient.post(endpoints.dois.configurations.activate(id));
    return response.data;
  },

  async deleteConfiguration(id) {
    const response = await apiClient.delete(endpoints.dois.configurations.delete(id));
    return response.data;
  },

  // Helper operations
  async getPatternPreviews() {
    const response = await apiClient.get(endpoints.dois.patternPreviews);
    return response.data;
  },

  async getSupportedAgencies() {
    const response = await apiClient.get(endpoints.dois.supportedAgencies);
    return response.data;
  },

  // DOI management operations
  async assignDOI(entityType, entityId, configurationId) {
    const response = await apiClient.post(endpoints.dois.assign, {
      entityType,
      entityId,
      configurationId,
    });
    return response.data;
  },

  async batchAssignDOIs(entities, configurationId) {
    const response = await apiClient.post(endpoints.dois.batchAssign, {
      entities,
      configurationId,
    });
    return response.data;
  },

  async unassignDOI(entityType, entityId) {
    const response = await apiClient.post(endpoints.dois.unassign, {
      entityType,
      entityId,
    });
    return response.data;
  },

  async regenerateDOI(entityType, entityId) {
    const response = await apiClient.post(endpoints.dois.regenerate, {
      entityType,
      entityId,
    });
    return response.data;
  },

  async getDOI(entityType, entityId) {
    const queryParams = new URLSearchParams({
      entityType,
      entityId,
    });
    
    const response = await apiClient.get(`${endpoints.dois.lookup}?${queryParams}`);
    return response.data;
  },

  async listDOIs(params = {}) {
    const { page = 1, limit = 20, entityType, status, configurationId } = params;
    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      ...(entityType && { entityType }),
      ...(status && { status }),
      ...(configurationId && { configurationId }),
    });
    
    const response = await apiClient.get(`${endpoints.dois.list}?${queryParams}`);
    return response.data;
  },

  async getDOIStatistics() {
    const response = await apiClient.get(endpoints.dois.statistics);
    return response.data;
  },

  // Deposit operations
  async depositDOI(doiId, immediate = true) {
    const response = await apiClient.post(endpoints.dois.deposit, {
      doiId,
      immediate,
    });
    return response.data;
  },

  async batchDepositDOIs(doiIds, configurationId, immediate = false) {
    const response = await apiClient.post(endpoints.dois.batchDeposit, {
      doiIds,
      configurationId,
      immediate,
    });
    return response.data;
  },

  async getDepositJobs(params = {}) {
    const { page = 1, limit = 20, status, configurationId } = params;
    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      ...(status && { status }),
      ...(configurationId && { configurationId }),
    });
    
    const response = await apiClient.get(`${endpoints.dois.depositJobs}?${queryParams}`);
    return response.data;
  },

  async retryFailedDeposits(configurationId) {
    const response = await apiClient.post(endpoints.dois.retryFailed, {
      configurationId,
    });
    return response.data;
  },

  async checkDOIStatus(doiId) {
    const response = await apiClient.get(endpoints.dois.checkStatus(doiId));
    return response.data;
  },

  // Export operations
  async exportDOIMetadata(doiId, format = 'xml') {
    const queryParams = new URLSearchParams({
      doiId,
      format,
    });
    
    const response = await apiClient.get(`${endpoints.dois.exportSingle}?${queryParams}`, {
      responseType: 'blob',
    });
    
    // Create download link
    const blob = new Blob([response.data], { 
      type: format === 'xml' ? 'application/xml' : 'application/json' 
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `doi_${doiId}.${format}`;
    link.click();
    window.URL.revokeObjectURL(url);
    
    return { success: true, message: 'DOI metadata exported successfully' };
  },

  async exportBatchDOIMetadata(entityType, format = 'xml') {
    const queryParams = new URLSearchParams({
      ...(entityType && { entityType }),
      format,
    });
    
    const response = await apiClient.get(`${endpoints.dois.exportBatch}?${queryParams}`, {
      responseType: 'blob',
    });
    
    // Create download link
    const blob = new Blob([response.data], { 
      type: format === 'xml' ? 'application/xml' : 'application/json' 
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `dois_batch_export.${format}`;
    link.click();
    window.URL.revokeObjectURL(url);
    
    return { success: true, message: 'Batch DOI metadata exported successfully' };
  },

  // Utility functions
  formatDOIStatus(status) {
    const statusMap = {
      NOT_ASSIGNED: { text: 'Not Assigned', color: 'default' },
      ASSIGNED: { text: 'Assigned', color: 'processing' },
      PENDING_DEPOSIT: { text: 'Pending Deposit', color: 'warning' },
      DEPOSITING: { text: 'Depositing', color: 'processing' },
      DEPOSITED: { text: 'Deposited', color: 'success' },
      FAILED: { text: 'Failed', color: 'error' },
      UPDATED: { text: 'Updated', color: 'success' },
    };
    
    return statusMap[status] || { text: status, color: 'default' };
  },

  formatDOIUrl(doi) {
    return doi ? `https://doi.org/${doi}` : null;
  },

  validateDOIPrefix(prefix) {
    // Basic DOI prefix validation (starts with 10.)
    const doiPrefixRegex = /^10\.\d+$/;
    return doiPrefixRegex.test(prefix);
  },
};